import { useState, useEffect, createContext, useContext } from 'react';
import { X, Cookie, Shield, FileText, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';

// Cookie Consent Context
const CookieConsentContext = createContext(null);

export const useCookieConsent = () => useContext(CookieConsentContext);

// Cookie categories for GDPR compliance
const COOKIE_CATEGORIES = {
  necessary: {
    id: 'necessary',
    name: 'Strictly Necessary',
    description: 'Essential for the website to function. Cannot be disabled.',
    required: true,
    cookies: ['session_id', 'csrf_token', 'cookie_consent']
  },
  analytics: {
    id: 'analytics',
    name: 'Analytics & Performance',
    description: 'Help us understand how visitors interact with our website by collecting anonymous information.',
    required: false,
    cookies: ['_ga', '_gid', 'posthog']
  },
  functional: {
    id: 'functional',
    name: 'Functional',
    description: 'Enable personalized features and remember your preferences.',
    required: false,
    cookies: ['language', 'theme', 'timezone']
  },
  marketing: {
    id: 'marketing',
    name: 'Marketing & Advertising',
    description: 'Used to deliver relevant advertisements and track campaign effectiveness.',
    required: false,
    cookies: ['_fbp', '_gcl_au']
  }
};

// Helper functions for cookie management
const setCookie = (name, value, days) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax; Secure`;
};

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
  return null;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
};

// Cookie Consent Provider
export const CookieConsentProvider = ({ children }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState({
    necessary: true,
    analytics: false,
    functional: false,
    marketing: false,
    timestamp: null,
    version: '1.0'
  });

  // Check if we're on admin pages - don't show cookie banner there
  const isAdminPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');

  useEffect(() => {
    // Skip cookie banner on admin pages
    if (isAdminPage) {
      setShowBanner(false);
      return;
    }
    
    const savedConsent = getCookie('cookie_consent');
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent);
        setConsent(parsed);
        setShowBanner(false);
        applyConsent(parsed);
      } catch {
        setShowBanner(true);
      }
    } else {
      setShowBanner(true);
      // Block all non-essential scripts until consent
      blockNonEssentialScripts();
    }
  }, []);

  const blockNonEssentialScripts = () => {
    // Disable PostHog until consent
    if (window.posthog) {
      window.posthog.opt_out_capturing();
    }
  };

  const applyConsent = (consentData) => {
    // Apply analytics consent
    if (consentData.analytics && window.posthog) {
      window.posthog.opt_in_capturing();
    } else if (window.posthog) {
      window.posthog.opt_out_capturing();
    }

    // Delete cookies for categories that are not consented
    if (!consentData.analytics) {
      COOKIE_CATEGORIES.analytics.cookies.forEach(deleteCookie);
    }
    if (!consentData.marketing) {
      COOKIE_CATEGORIES.marketing.cookies.forEach(deleteCookie);
    }
    if (!consentData.functional) {
      COOKIE_CATEGORIES.functional.cookies.forEach(deleteCookie);
    }
  };

  const saveConsent = (newConsent) => {
    const consentData = {
      ...newConsent,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    setCookie('cookie_consent', JSON.stringify(consentData), 365);
    setConsent(consentData);
    setShowBanner(false);
    setShowSettings(false);
    applyConsent(consentData);
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      functional: true,
      marketing: true
    });
  };

  const acceptNecessaryOnly = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      functional: false,
      marketing: false
    });
  };

  const savePreferences = () => {
    saveConsent(consent);
  };

  const withdrawConsent = () => {
    deleteCookie('cookie_consent');
    setConsent({
      necessary: true,
      analytics: false,
      functional: false,
      marketing: false,
      timestamp: null,
      version: '1.0'
    });
    setShowBanner(true);
    blockNonEssentialScripts();
  };

  const updateCategory = (category, value) => {
    if (category === 'necessary') return; // Cannot disable necessary cookies
    setConsent(prev => ({ ...prev, [category]: value }));
  };

  return (
    <CookieConsentContext.Provider value={{ consent, withdrawConsent, showSettings: () => setShowSettings(true) }}>
      {children}
      
      {/* Cookie Consent Banner */}
      {showBanner && !showSettings && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center p-4" data-testid="cookie-banner">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-accent-teal/10 rounded-full">
                  <Cookie className="w-6 h-6 text-accent-teal" />
                </div>
                <div>
                  <h2 className="font-serif text-2xl text-ink mb-2">We Value Your Privacy</h2>
                  <p className="text-ink-muted text-sm">
                    We use cookies to enhance your browsing experience, provide personalized content, and analyze our traffic. 
                    In accordance with GDPR, we need your consent to use non-essential cookies.
                  </p>
                </div>
              </div>

              <div className="bg-cream/50 rounded-lg p-4 mb-6">
                <p className="text-sm text-ink-light">
                  By clicking "Accept All", you consent to our use of cookies. You can customize your preferences by clicking "Cookie Settings". 
                  You may withdraw your consent at any time. For more information, please read our{' '}
                  <a href="/privacy-policy" className="text-accent-teal underline">Privacy Policy</a> and{' '}
                  <a href="/cookie-policy" className="text-accent-teal underline">Cookie Policy</a>.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={acceptNecessaryOnly}
                  variant="outline"
                  className="flex-1 border-ink/20 text-ink hover:bg-ink/5"
                  data-testid="reject-non-essential-btn"
                >
                  Necessary Only
                </Button>
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="outline"
                  className="flex-1 border-ink/20 text-ink hover:bg-ink/5"
                  data-testid="cookie-settings-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Cookie Settings
                </Button>
                <Button
                  onClick={acceptAll}
                  className="flex-1 bg-ink text-parchment hover:bg-ink-light"
                  data-testid="accept-all-btn"
                >
                  Accept All
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4" data-testid="cookie-settings-modal">
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-warm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="w-6 h-6 text-accent-teal" />
                  <h2 className="font-serif text-2xl text-ink">Privacy Preferences</h2>
                </div>
                <button 
                  onClick={() => setShowSettings(false)}
                  className="p-2 hover:bg-ink/5 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-ink-muted text-sm mt-2">
                Manage your cookie preferences below. Strictly necessary cookies cannot be disabled as they are essential for the website to function.
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {Object.values(COOKIE_CATEGORIES).map((category) => (
                <CookieCategoryCard
                  key={category.id}
                  category={category}
                  enabled={consent[category.id]}
                  onToggle={(value) => updateCategory(category.id, value)}
                />
              ))}
            </div>

            <div className="p-6 border-t border-warm bg-cream/30">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={acceptNecessaryOnly}
                  variant="outline"
                  className="flex-1 border-ink/20"
                >
                  Reject All Non-Essential
                </Button>
                <Button
                  onClick={savePreferences}
                  className="flex-1 bg-ink text-parchment hover:bg-ink-light"
                  data-testid="save-preferences-btn"
                >
                  Save Preferences
                </Button>
                <Button
                  onClick={acceptAll}
                  variant="outline"
                  className="flex-1 border-accent-teal text-accent-teal hover:bg-accent-teal/10"
                >
                  Accept All
                </Button>
              </div>
              <p className="text-xs text-ink-muted text-center mt-4">
                Your preferences will be saved for 12 months. You can change them anytime via the cookie settings link in the footer.
              </p>
            </div>
          </div>
        </div>
      )}
    </CookieConsentContext.Provider>
  );
};

// Cookie Category Card Component
const CookieCategoryCard = ({ category, enabled, onToggle }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-warm rounded-lg overflow-hidden">
      <div className="p-4 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-ink">{category.name}</h3>
              {category.required && (
                <span className="text-xs bg-ink/10 text-ink px-2 py-0.5 rounded">Always Active</span>
              )}
            </div>
            <p className="text-sm text-ink-muted mt-1">{category.description}</p>
          </div>
          <div className="flex items-center gap-3 ml-4">
            <Switch
              checked={enabled}
              onCheckedChange={onToggle}
              disabled={category.required}
              className="data-[state=checked]:bg-accent-teal"
            />
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1 hover:bg-ink/5 rounded"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
      
      {expanded && (
        <div className="px-4 pb-4 bg-cream/30">
          <p className="text-xs text-ink-muted mb-2">Cookies in this category:</p>
          <div className="flex flex-wrap gap-2">
            {category.cookies.map((cookie) => (
              <span key={cookie} className="text-xs bg-white px-2 py-1 rounded border border-warm">
                {cookie}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CookieConsentProvider;
